import { createElement, useState, useCallback } from "react";
import { getApiUrl } from "src/api/axiosClient/utils";
import { useSnackbar } from "src/utils/useSnackbar";
import i18next from "i18next";

export type PdfHeaderConfig = {
    enabled?: boolean;
    logoUrl?: string;
    logoHeight?: number;
    title?: string;
    subtitle?: string;
    alignment?: "left" | "center" | "right";
};

export type PdfFooterConfig = {
    enabled?: boolean;
    showPageNumbers?: boolean;
    pageNumberFormat?: "page" | "pageOfTotal";
    text?: string;
    alignment?: "left" | "center" | "right" | "spread";
};

export type PdfExportAuth = {
    localStorage?: Record<string, string>;
    sessionStorage?: Record<string, string>;
    cookies?: Record<string, unknown>[];
};

export type PdfExportOptions = {
    format?: "A4" | "A3" | "Letter" | "Legal";
    landscape?: boolean;
    printBackground?: boolean;
    waitForTimeout?: number;
    scale?: number;
    debug?: boolean;
    debugScreenshot?: boolean;
    margin?: {
        top?: string;
        right?: string;
        bottom?: string;
        left?: string;
    };
    auth?: PdfExportAuth;
    pdfHeader?: PdfHeaderConfig;
    pdfFooter?: PdfFooterConfig;
    forceCms?: boolean;
};

type PuckPdfSettings = {
    format?: "A4" | "A3" | "Letter" | "Legal";
    landscape?: "portrait" | "landscape";
    marginsMm?: {
        top?: number;
        right?: number;
        bottom?: number;
        left?: number;
    };
};

type PuckRootPdfProps = {
    pdfSettings?: PuckPdfSettings;
    pdfHeader?: PdfHeaderConfig;
    pdfFooter?: PdfFooterConfig;
    score?: string | string[];
};

export const buildPuckPdfExportUrl = (
    pageUrl: string,
    params: {
        selectedCompanyId?: string | number | null | undefined;
        score?: string | string[] | null | undefined;
        previewSlug?: string | null | undefined;
    },
): string => {
    const url = new URL(pageUrl, window.location.origin);

    const selectedCompanyId = params.selectedCompanyId;
    if (selectedCompanyId !== null && selectedCompanyId !== undefined) {
        url.searchParams.set("selected_entity_id", String(selectedCompanyId));
        url.searchParams.set("selected_organization_id", String(selectedCompanyId));
    }

    const score = params.score;
    if (typeof score === "string" && score.trim() !== "") {
        url.searchParams.set("score_id", score);
    }
    if (Array.isArray(score) && score.length === 1 && typeof score[0] === "string" && score[0].trim() !== "") {
        url.searchParams.set("score_id", score[0]);
    }

    const previewSlug = params.previewSlug;
    if (typeof previewSlug === "string" && previewSlug.trim() !== "") {
        url.searchParams.set("preview_slug", previewSlug.trim());
    }

    return url.toString();
};

export const getPdfExportOptionsFromPuckRootProps = (
    rootProps: PuckRootPdfProps | null | undefined,
): PdfExportOptions => {
    const pdfSettings = rootProps?.pdfSettings;
    const marginsMm = pdfSettings?.marginsMm;
    const margin =
        marginsMm ?
            {
                top: typeof marginsMm.top === "number" ? `${marginsMm.top}mm` : undefined,
                right: typeof marginsMm.right === "number" ? `${marginsMm.right}mm` : undefined,
                bottom: typeof marginsMm.bottom === "number" ? `${marginsMm.bottom}mm` : undefined,
                left: typeof marginsMm.left === "number" ? `${marginsMm.left}mm` : undefined,
            }
        :   undefined;

    const hasMargin =
        margin !== undefined &&
        (margin.top !== undefined ||
            margin.right !== undefined ||
            margin.bottom !== undefined ||
            margin.left !== undefined);

    const pdfHeader = rootProps?.pdfHeader?.enabled === true ? rootProps.pdfHeader : undefined;
    const pdfFooter = rootProps?.pdfFooter?.enabled === true ? rootProps.pdfFooter : undefined;

    return {
        format: "A3",
        landscape: pdfSettings?.landscape === "landscape",
        printBackground: true,
        waitForTimeout: 60000,
        margin: hasMargin ? margin : undefined,
        pdfHeader,
        pdfFooter,
    };
};

const getStorageValue = (key: string): string | null => localStorage.getItem(key);

const getSelectedEntityIdFromUrl = (url: URL): string | null => {
    return (
        url.searchParams.get("selected_entity_id") ||
        url.searchParams.get("selected_organization_id") ||
        url.searchParams.get("selected_company_id")
    );
};

export const buildPdfExportLocalStoragePayload = (targetUrl: URL): Record<string, string> => {
    const entityId = getSelectedEntityIdFromUrl(targetUrl) || getStorageValue("entity") || getStorageValue("company");
    const campaignId = getStorageValue("currentCampaignId");
    const language = getStorageValue("foodpilot_user_language") || i18next.resolvedLanguage || i18next.language;
    const languages = getStorageValue("foodpilot_languages");

    const entries: [string, string | null | undefined][] = [
        ["entity", entityId],
        ["company", entityId],
        // The PDF is rendered by a headless browser that re-loads the page. Auth rides on the
        // JWT cookies, but impersonation rides on this localStorage key (read per-request by
        // handleImpersonation to set X-SWITCH-USER). Without it the render runs as the real
        // admin instead of the impersonated user, and the PDF shows the wrong company.
        ["connectedAs", getStorageValue("connectedAs")],
        ["currentCampaignId", campaignId],
        ["foodpilot_user_language", language],
        ["foodpilot_languages", languages],
    ];

    const payload: Record<string, string> = {};
    for (const [key, value] of entries) {
        if (typeof value === "string" && value.trim() !== "") {
            payload[key] = value;
        }
    }
    return payload;
};

export const buildPdfExportTargetUrl = (url: string, options: PdfExportOptions): URL => {
    const targetUrl = new URL(url, window.location.origin);
    targetUrl.searchParams.set("__pdf", "1");

    const forceCms = options.forceCms ?? true;
    if (forceCms) {
        targetUrl.searchParams.set("__force_cms", "1");
    } else {
        targetUrl.searchParams.delete("__force_cms");
    }

    if (options.debug) {
        targetUrl.searchParams.set("__pdf_debug", "1");
        if (options.debugScreenshot) {
            targetUrl.searchParams.set("__pdf_debug_screenshot", "1");
        }
    }

    const entityId = getSelectedEntityIdFromUrl(targetUrl) || getStorageValue("entity") || getStorageValue("company");
    const campaignId = getStorageValue("currentCampaignId");

    if (entityId && !targetUrl.searchParams.get("selected_entity_id")) {
        targetUrl.searchParams.set("selected_entity_id", entityId);
    }
    if (entityId && !targetUrl.searchParams.get("selected_organization_id")) {
        targetUrl.searchParams.set("selected_organization_id", entityId);
    }
    if (campaignId && !targetUrl.searchParams.get("campaign_id")) {
        targetUrl.searchParams.set("campaign_id", campaignId);
    }

    return targetUrl;
};

export const usePdfExport = () => {
    const [isExporting, setIsExporting] = useState(false);
    const { setSnackbarProps } = useSnackbar();

    const exportPage = useCallback(
        async (url: string, filename?: string, options: PdfExportOptions = {}) => {
            setIsExporting(true);
            try {
                const apiUrl = getApiUrl();
                const targetUrl = buildPdfExportTargetUrl(url, options);
                const localStoragePayload = buildPdfExportLocalStoragePayload(targetUrl);
                const authFromLocalStorage =
                    Object.keys(localStoragePayload).length > 0 ? { localStorage: localStoragePayload } : undefined;
                const auth = options.auth ?? authFromLocalStorage;

                const response = await fetch(`${apiUrl}/api/export/pdf`, {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        url: targetUrl.toString(),
                        filename,
                        format: options.format,
                        landscape: options.landscape,
                        printBackground: options.printBackground,
                        waitForTimeout: options.waitForTimeout,
                        scale: options.scale,
                        margin: options.margin,
                        auth,
                        debug: options.debug === true,
                        debugScreenshot: options.debugScreenshot === true,
                        forceCms: options.forceCms ?? true,
                        pdfHeader: options.pdfHeader,
                        pdfFooter: options.pdfFooter,
                    }),
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("PDF export error response:", {
                        status: response.status,
                        statusText: response.statusText,
                        body: errorText,
                    });

                    try {
                        const error = JSON.parse(errorText);
                        throw new Error(error.message || error.error || `Failed to export PDF (${response.status})`);
                    } catch (e) {
                        throw new Error(`Failed to export PDF: ${response.status} ${response.statusText}`, {
                            cause: e,
                        });
                    }
                }

                // Télécharger le PDF
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = downloadUrl;
                a.download = filename || "export.pdf";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(downloadUrl);

                setSnackbarProps({
                    message: createElement(
                        "div",
                        null,
                        createElement(
                            "div",
                            { style: { fontWeight: 700 } },
                            i18next.t("pdf.export.download_success", "PDF downloaded"),
                        ),
                        createElement("div", null, filename || "export.pdf"),
                    ),
                    type: "success",
                });
            } catch (error) {
                setSnackbarProps({
                    message: error instanceof Error ? error.message : "Erreur lors de l'export PDF",
                    type: "error",
                });
                throw error;
            } finally {
                setIsExporting(false);
            }
        },
        [setSnackbarProps],
    );

    return {
        exportPage,
        isExporting,
    };
};
